import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Calendar, Compass, Utensils, Plus, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  {
    value: 'event',
    label: 'Event',
    description: 'Ticketed party, concert, festival, brunch. Buyers pick from ticket tiers.',
    Icon: Calendar,
    accent: 'indigo',
  },
  {
    value: 'experience',
    label: 'Experience',
    description: 'Tour, tasting, class. Buyers book a time-slot at a fixed duration.',
    Icon: Compass,
    accent: 'emerald',
  },
  {
    value: 'reservation',
    label: 'Reservation',
    description: 'Dinner / table booking. Buyers pick a party size, date and time.',
    Icon: Utensils,
    accent: 'amber',
  },
];

const accentMap = {
  indigo: 'border-indigo-500 ring-2 ring-indigo-500/40 bg-indigo-500/5',
  emerald: 'border-emerald-500 ring-2 ring-emerald-500/40 bg-emerald-500/5',
  amber: 'border-amber-500 ring-2 ring-amber-500/40 bg-amber-500/5',
};

const iconColor = {
  indigo: 'text-indigo-500',
  emerald: 'text-emerald-500',
  amber: 'text-amber-500',
};

/**
 * Renders the "Type" picker (Event | Experience | Reservation) at the very top
 * of the event-create form. Surfaces inline kind-specific fields so the form
 * adapts as soon as the organizer changes the type.
 */
export function EventTypePicker({ kind, onChange }) {
  const current = kind || 'event';
  return (
    <div className="space-y-2" data-testid="event-type-picker">
      <Label>Type *</Label>
      <p className="text-[11px] text-muted-foreground">
        Pick how this gets sold. You can change this anytime before going live.
      </p>
      <div className="grid sm:grid-cols-3 gap-3">
        {TYPE_OPTIONS.map(opt => {
          const active = current === opt.value;
          const { Icon } = opt;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              data-testid={`event-type-${opt.value}`}
              className={[
                'text-left p-4 rounded-xl border transition-all',
                active
                  ? accentMap[opt.accent]
                  : 'border-border hover:border-muted-foreground/40 hover:bg-muted/40',
              ].join(' ')}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`h-4 w-4 ${active ? iconColor[opt.accent] : 'text-muted-foreground'}`} />
                <span className="font-semibold text-sm">{opt.label}</span>
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">
                {opt.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Cancellation Policy picker. Loads industry-standard presets from the
 * backend (Flexible / Moderate / Strict / All Sales Final) and renders
 * each tier so the organizer sees exactly what guests are agreeing to.
 *
 * Stores the FULL policy dict on formData so the backend doesn't have to
 * re-resolve presets at write time.
 */
export function CancellationPolicyPicker({ policy, onChange }) {
  const [presets, setPresets] = useState([]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/event-kinds/cancellation-presets`)
      .then(res => { if (alive) setPresets(res.data?.presets || []); })
      .catch(() => { /* offline-safe — falls back to empty list */ });
    return () => { alive = false; };
  }, []);

  const currentType = (policy && policy.type) || 'flexible';
  const choose = (preset) => onChange({ ...preset });

  return (
    <div className="space-y-3" data-testid="cancellation-policy-picker">
      <div>
        <Label>Cancellation Policy</Label>
        <p className="text-[11px] text-muted-foreground">
          Modeled after Viator + Airbnb Experiences. Refunds are calculated
          off the slot start time.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {presets.map(p => {
          const active = currentType === p.type;
          return (
            <button
              key={p.type}
              type="button"
              onClick={() => choose(p)}
              data-testid={`cancel-policy-${p.type}`}
              className={[
                'text-left p-3 rounded-lg border transition-all text-sm',
                active
                  ? 'border-indigo-500 ring-2 ring-indigo-500/40 bg-indigo-500/5'
                  : 'border-border hover:border-muted-foreground/40 hover:bg-muted/40',
              ].join(' ')}
            >
              <div className="font-semibold mb-1">{p.label}</div>
              <p className="text-[11px] text-muted-foreground leading-snug">
                {p.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Experience-specific fields (duration, lead time, meeting point, what
 * to bring, itinerary). Modeled on Airbnb Experience + Viator product
 * standards.
 */
export function ExperienceFields({ formData, setFormData }) {
  const itinerary = formData.itinerary || [];
  const updateItinerary = (next) => setFormData({ ...formData, itinerary: next });
  const addStep = () => updateItinerary([...itinerary, { title: '', duration_minutes: '', description: '' }]);
  const removeStep = (idx) => updateItinerary(itinerary.filter((_, i) => i !== idx));
  const setStep = (idx, patch) => updateItinerary(itinerary.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  return (
    <div className="space-y-4" data-testid="experience-fields">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Duration (minutes) *</Label>
          <Input
            type="number"
            min={15}
            value={formData.duration_minutes || ''}
            onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
            placeholder="120"
            data-testid="exp-duration"
          />
        </div>
        <div className="space-y-2">
          <Label>Booking cutoff (hours)</Label>
          <Input
            type="number"
            min={0}
            value={formData.lead_time_hours ?? 0}
            onChange={(e) => setFormData({ ...formData, lead_time_hours: e.target.value })}
            placeholder="4"
            data-testid="exp-lead-time"
          />
          <p className="text-[10px] text-muted-foreground">Minutes before start that bookings close.</p>
        </div>
        <div className="space-y-2">
          <Label>Max group size</Label>
          <Input
            type="number"
            min={1}
            value={formData.max_group_size || ''}
            onChange={(e) => setFormData({ ...formData, max_group_size: e.target.value })}
            placeholder="12"
            data-testid="exp-max-group"
          />
        </div>
        <div className="space-y-2">
          <Label>Minimum age</Label>
          <Input
            type="number"
            min={0}
            value={formData.min_age || ''}
            onChange={(e) => setFormData({ ...formData, min_age: e.target.value })}
            placeholder="21"
            data-testid="exp-min-age"
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Languages</Label>
          <Input
            value={(formData.languages || []).join(', ')}
            onChange={(e) => setFormData({
              ...formData,
              languages: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
            })}
            placeholder="English, Spanish"
            data-testid="exp-languages"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Meeting point</Label>
        <Textarea
          rows={2}
          value={formData.meeting_point || ''}
          onChange={(e) => setFormData({ ...formData, meeting_point: e.target.value })}
          placeholder="Front entrance of the wine cellar, 123 Vine St"
          data-testid="exp-meeting-point"
        />
      </div>

      <div className="space-y-2">
        <Label>What to bring</Label>
        <Input
          value={(formData.what_to_bring || []).join(', ')}
          onChange={(e) => setFormData({
            ...formData,
            what_to_bring: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
          })}
          placeholder="Comfortable shoes, water bottle, camera"
          data-testid="exp-what-to-bring"
        />
        <p className="text-[10px] text-muted-foreground">Comma-separated.</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Itinerary</Label>
          <Button type="button" size="sm" variant="outline" onClick={addStep} data-testid="exp-add-itin-step">
            <Plus className="h-3 w-3 mr-1" /> Add step
          </Button>
        </div>
        {itinerary.length === 0 && (
          <p className="text-[11px] text-muted-foreground">
            Airbnb requires ≥3 sequenced steps. Walk guests through what happens minute by minute.
          </p>
        )}
        <div className="space-y-2">
          {itinerary.map((step, idx) => (
            <div key={idx} className="rounded-lg border p-3 space-y-2 bg-muted/20" data-testid={`exp-itin-row-${idx}`}>
              <div className="flex items-start gap-2">
                <div className="text-xs font-mono text-muted-foreground pt-2">#{idx + 1}</div>
                <div className="flex-1 grid sm:grid-cols-[1fr,140px] gap-2">
                  <Input
                    value={step.title || ''}
                    onChange={(e) => setStep(idx, { title: e.target.value })}
                    placeholder="Welcome & vineyard intro"
                    data-testid={`exp-itin-title-${idx}`}
                  />
                  <Input
                    type="number"
                    min={1}
                    value={step.duration_minutes || ''}
                    onChange={(e) => setStep(idx, { duration_minutes: e.target.value })}
                    placeholder="15 min"
                    data-testid={`exp-itin-duration-${idx}`}
                  />
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-muted-foreground"
                  onClick={() => removeStep(idx)}
                  data-testid={`exp-itin-remove-${idx}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Textarea
                rows={2}
                value={step.description || ''}
                onChange={(e) => setStep(idx, { description: e.target.value })}
                placeholder="Short description (optional)"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Reservation-specific fields (party size range, large-party deposit,
 * turn time, seating areas). Modeled on OpenTable + Resy controls.
 */
export function ReservationFields({ formData, setFormData }) {
  const depositDollars = (formData.deposit_amount_cents || 0) / 100;
  return (
    <div className="space-y-4" data-testid="reservation-fields">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Min party size</Label>
          <Input
            type="number"
            min={1}
            value={formData.party_size_min ?? 1}
            onChange={(e) => setFormData({ ...formData, party_size_min: e.target.value })}
            data-testid="res-party-min"
          />
        </div>
        <div className="space-y-2">
          <Label>Max party size</Label>
          <Input
            type="number"
            min={1}
            value={formData.party_size_max ?? 8}
            onChange={(e) => setFormData({ ...formData, party_size_max: e.target.value })}
            data-testid="res-party-max"
          />
        </div>
        <div className="space-y-2">
          <Label>Turn time (minutes)</Label>
          <Input
            type="number"
            min={15}
            value={formData.turn_time_minutes ?? 90}
            onChange={(e) => setFormData({ ...formData, turn_time_minutes: e.target.value })}
            data-testid="res-turn-time"
          />
          <p className="text-[10px] text-muted-foreground">How long each table is held.</p>
        </div>
        <div className="space-y-2">
          <Label>Large-party threshold</Label>
          <Input
            type="number"
            min={0}
            value={formData.large_party_threshold || ''}
            onChange={(e) => setFormData({ ...formData, large_party_threshold: e.target.value })}
            placeholder="6"
            data-testid="res-large-threshold"
          />
          <p className="text-[10px] text-muted-foreground">Party size at/above which a deposit is required.</p>
        </div>
        <div className="space-y-2">
          <Label>Deposit per guest (USD)</Label>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={depositDollars || ''}
            onChange={(e) => {
              const v = parseFloat(e.target.value || '0');
              setFormData({
                ...formData,
                deposit_amount_cents: Math.max(0, Math.round((isNaN(v) ? 0 : v) * 100)),
              });
            }}
            placeholder="25.00"
            data-testid="res-deposit"
          />
        </div>
        <div className="space-y-2">
          <Label>Dress code</Label>
          <Input
            value={formData.dress_code || ''}
            onChange={(e) => setFormData({ ...formData, dress_code: e.target.value })}
            placeholder="Smart casual"
            data-testid="res-dress-code"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Seating areas</Label>
        <Input
          value={(formData.seating_areas || []).join(', ')}
          onChange={(e) => setFormData({
            ...formData,
            seating_areas: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
          })}
          placeholder="Patio, Bar, Main dining, Private room"
          data-testid="res-seating-areas"
        />
        <p className="text-[10px] text-muted-foreground">Comma-separated. Guests can pick a preferred area at checkout.</p>
      </div>
    </div>
  );
}
